/**
 * Centralized Logger Utility
 * ===========================
 * THIS IS THE STANDARD LOGGING UTILITY FOR THE PROJECT
 * 
 * Provides conditional logging that's automatically disabled in production.
 * All development logging should use this utility instead of direct console.log calls.
 * 
 * Benefits:
 * - Automatically disabled in production builds (except errors)
 * - Consistent logging format across the application
 * - Easy to add additional logging features (timestamps, levels, etc.)
 * - Prevents sensitive data from leaking in production logs
 * 
 * Usage Examples:
 * ---------------
 * import { logger } from '../utils/logger';
 * 
 * // Development-only logs (automatically filtered out in production)
 * logger.log('Debug message');           // General logging
 * logger.info('Information message');    // Informational messages
 * logger.debug('Debug details', obj);    // Debug information
 * logger.warn('Warning message');        // Warning messages
 * 
 * // Production-safe error logging (always displayed)
 * logger.error('Error occurred', error); // Error messages
 * 
 * // Structured logging
 * logger.group('API Request');
 * logger.log('URL:', url);
 * logger.log('Data:', data);
 * logger.groupEnd();
 * 
 * logger.table(arrayOfObjects);          // Display data in table format
 * 
 * When NOT to use console.log directly:
 * -------------------------------------
 * ❌ DON'T: console.log('Debug info')         // Not filtered in production
 * ✅ DO:    logger.log('Debug info')          // Automatically filtered
 * 
 * ❌ DON'T: console.error('Error')            // Use for errors only
 * ✅ DO:    logger.error('Error')             // Better for error tracking
 * 
 * Migration Guide:
 * ---------------
 * Replace all console.log/info/debug/warn calls with logger equivalents.
 * Keep logger.error for errors to ensure they're always visible.
 */

const isDevelopment = process.env.NODE_ENV !== 'production';

export const logger = {
  /**
   * Development-only logging
   * @param  {...any} args - Arguments to log
   */
  log: (...args) => {
    if (isDevelopment) {
      console.log(...args);
    }
  },

  /**
   * Development-only info logging
   * @param  {...any} args - Arguments to log
   */
  info: (...args) => {
    if (isDevelopment) {
      console.info(...args);
    }
  },

  /**
   * Development-only debug logging
   * @param  {...any} args - Arguments to log
   */
  debug: (...args) => {
    if (isDevelopment) {
      console.debug(...args);
    }
  },

  /**
   * Development-only warning logging
   * @param  {...any} args - Arguments to log
   */
  warn: (...args) => {
    if (isDevelopment) {
      console.warn(...args);
    }
  },

  /**
   * Error logging (always enabled, even in production)
   * @param  {...any} args - Arguments to log
   */
  error: (...args) => {
    console.error(...args);
  },

  /**
   * Group logging (development only)
   * @param {string} label - Group label
   */
  group: (label) => {
    if (isDevelopment) {
      console.group(label);
    }
  },

  /**
   * End group logging (development only)
   */
  groupEnd: () => {
    if (isDevelopment) {
      console.groupEnd();
    }
  },

  /**
   * Table logging for objects/arrays (development only)
   * @param {*} data - Data to display in table format
   */
  table: (data) => {
    if (isDevelopment) {
      console.table(data);
    }
  }
};

export default logger;
